import React, { useMemo } from 'react';
import { AdminOrg } from 'new/stores/adminOrgs/adminOrgs.types';
import {
  getOrgFilterStatus,
  orgMatchesPlanFilter,
  PLAN_FILTER_ALL,
  StatusFilterValue,
} from './utils';
import styles from './CustomerOrgs.module.scss';

interface PopularFilterPreset {
  key: string;
  label: string;
  status: StatusFilterValue;
  plan: string;
}

const PRESETS: PopularFilterPreset[] = [
  { key: 'all', label: 'All', status: 'ALL', plan: PLAN_FILTER_ALL },
  { key: 'active', label: 'Active', status: 'ACTIVE', plan: PLAN_FILTER_ALL },
  { key: 'paid', label: 'Paid', status: 'ACTIVE', plan: 'PAID' },
  { key: 'suspended', label: 'Suspended', status: 'SUSPENDED', plan: PLAN_FILTER_ALL },
  { key: 'deactivated', label: 'Deactivated', status: 'DEACTIVATED', plan: PLAN_FILTER_ALL },
];

interface PopularFiltersProps {
  orgs: AdminOrg[];
  statusFilter: StatusFilterValue;
  planFilter: string;
  onChange: (status: StatusFilterValue, plan: string) => void;
}

/**
 * Quick presets combining status + plan in one click — e.g. "Paid" means active AND on a
 * paid plan, not just a plan filter. Distinct from (and coexists with) the Status/Plan
 * column-header filters, which stay independently adjustable for granular cases these
 * presets don't cover. A preset shows as selected only when both dimensions match exactly;
 * manually adjusting a column filter away from a preset just means no pill is highlighted.
 */
export function PopularFilters({ orgs, statusFilter, planFilter, onChange }: PopularFiltersProps) {
  const counts = useMemo(() => {
    const result: Record<string, number> = {};
    PRESETS.forEach((preset) => {
      result[preset.key] = orgs.filter(
        (org) =>
          (preset.status === 'ALL' || getOrgFilterStatus(org) === preset.status) &&
          orgMatchesPlanFilter(org, preset.plan),
      ).length;
    });
    return result;
  }, [orgs]);

  return (
    <div className={styles.statusFilter}>
      {PRESETS.map((preset) => {
        const isActive = preset.status === statusFilter && preset.plan === planFilter;
        return (
          <button
            key={preset.key}
            type="button"
            className={isActive ? styles.statusFilterButtonActive : styles.statusFilterButton}
            onClick={() => onChange(preset.status, preset.plan)}
          >
            {preset.label} <span className={styles.statusFilterCount}>{counts[preset.key]}</span>
          </button>
        );
      })}
    </div>
  );
}
